import {
  ChangeEvent,
  ComponentType,
  FunctionComponent,
  Ref,
  useEffect,
  useRef,
  useState,
} from 'react'
import { css, cx } from '@emotion/css'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import ReCAPTCHAImport from 'react-google-recaptcha'
import { useTranslation } from 'react-i18next'

// react-google-recaptcha's typings reference an older @types/react bundled
// with @types/react-dom; narrow the cast to the props we actually pass so
// future regressions (e.g. removing onChange) are still caught by tsc.
type ReCAPTCHAInstance = InstanceType<typeof ReCAPTCHAImport>
type ReCAPTCHAProps = {
  sitekey: string
  size?: 'invisible' | 'normal' | 'compact'
  onChange?: (token: string | null) => void
  ref?: Ref<ReCAPTCHAInstance>
}
const ReCAPTCHA = ReCAPTCHAImport as unknown as ComponentType<ReCAPTCHAProps>
import type { GiftCard, GiftCardRequest } from '@/libs/helpers/apiClient'
import { Button, CheckBox, CircularLoader, Divider, formStyles, Grid, SvgIcon, Text } from '@/tbui'

const StyledButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 34px;
  width: 88px;
  margin-top: 25px;
`

const ToolTipContent = styled.span`
  visibility: hidden;
  background-color: ${(props) => props.theme.palette.TEXT};
  color: ${(props) => props.theme.palette.WHITE};
  border-radius: 6px;
  position: absolute;
  z-index: 1;
  margin-top: 20px;
  left: 0;
  padding: 12px 20px;
  text-align: left;
  line-height: 20px;
  font-size: 13px;
  @media (max-width: ${(props) => props.theme.breakpoints.MD}) {
    width: 250px;
  }
  @media (min-width: ${(props) => props.theme.breakpoints.MD}) {
    width: 370px;
  }
`

const ToolTip = styled.div`
  position: absolute;
  display: inline-block;
  &:hover span {
    visibility: visible;
  }
`

const StyledLabel = styled.label`
  ${({ theme }) => formStyles(theme).label}
  display: block;
  margin-bottom: 7px;
`

const StyledGrid = styled(Grid)`
  align-items: top;
  ${(props) => `@media (max-width: ${props.theme.breakpoints.SM}) {
    display: contents;

    button {
      width: 100%;
      margin-top: 5px;
      max-width: 30%;
    }
  }
  `}
`

const StyledGiftCardContainer = styled(Grid)`
  padding-bottom: 16px;
  grid-template-columns: auto 1fr;
  justify-content: left;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid ${(props) => props.theme.palette.GRAY_1};
`

const StyledReCAPTCHA = styled(ReCAPTCHA)`
  z-index: 10;
`

interface DigitalCardProps {
  title: string
  isCartLoading?: boolean
  handleDGCChange: (e: ChangeEvent<HTMLInputElement>) => void
  giftCardOptions: GiftCard[]
  isGiftCardPicked: boolean
  selectedGiftCardCode?: string | null
  handleAddGiftCard: ({ code, recaptchaToken, PIN }: GiftCardRequest) => void
  handleRemoveGiftCard: () => void
  applyGiftCardError: string | null
  isECardsEnabled: boolean
}

const GiftCardPicker: FunctionComponent<DigitalCardProps> = ({
  title,
  isCartLoading,
  handleDGCChange,
  giftCardOptions,
  isGiftCardPicked,
  selectedGiftCardCode,
  handleAddGiftCard,
  handleRemoveGiftCard,
  applyGiftCardError,
  isECardsEnabled = false,
}) => {
  const noGiftCardOptions = giftCardOptions.length === 0
  // HOOKS
  const { t } = useTranslation()
  const theme = useTheme()
  // STATES
  const [code, setCode] = useState('')
  const [PIN, setPIN] = useState<string | null>(null)
  const recaptchaRef = useRef<ReCAPTCHAInstance>(null)
  const codeMaxLength = isECardsEnabled ? 32 : 10
  const codeMinLength = isECardsEnabled ? 8 : 1

  const hasError = applyGiftCardError != null
  const isInputDisabled = isGiftCardPicked || isCartLoading
  const isButtonDisabled =
    isInputDisabled || code.length < codeMinLength || code.length > codeMaxLength

  const checkOneOption = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e && !isCartLoading) {
      e.persist()
      handleDGCChange(e)
    }
  }

  const onSubmit = async (): Promise<void> => {
    const recaptchaToken = await recaptchaRef.current?.executeAsync()
    if (recaptchaToken == null) {
      return
    }
    handleAddGiftCard({ code, recaptchaToken, PIN })
  }

  useEffect(() => {
    if (isGiftCardPicked) {
      setCode('')
      setPIN(null)
    }
  }, [isGiftCardPicked])

  return (
    <div data-test="gift-card-picker">
      <Text type="H4" m={`36px 0 ${noGiftCardOptions ? '24px' : '12px'} 0`}>
        {isECardsEnabled ? t('checkout.payment.pay_gift_card_bill') : title}
      </Text>
      {!noGiftCardOptions &&
        (!isECardsEnabled
          ? giftCardOptions
              .filter((giftCard) => giftCard.code !== '')
              .map((giftCard) => (
                <Grid key={giftCard.code}>
                  <Grid
                    template="auto 1fr"
                    gap="16px"
                    align="center"
                    m="12px 0 15px 5px"
                    data-test={`gift-card-${giftCard.code}`}
                  >
                    <CheckBox
                      onChange={checkOneOption}
                      isSelected={giftCard.code === selectedGiftCardCode}
                      id={giftCard.code}
                      variant="ADD"
                      isDisabled={isCartLoading}
                    />
                    <Grid gap="6px">
                      <Text m="0 0 5px 0">
                        {t('checkout.payment.option_gift_card', {
                          amount: giftCard.currentValue,
                        })}
                      </Text>
                    </Grid>
                  </Grid>
                  <Divider />
                </Grid>
              ))
          : !isCartLoading && (
              <StyledGiftCardContainer data-test={`gift-card-value-${giftCardOptions[0].code}`}>
                <SvgIcon
                  name="icon_delete"
                  fontSize="24px"
                  color={'PRIMARY'}
                  onClick={handleRemoveGiftCard}
                />
                <Grid gap="6px">
                  <Text>
                    {t('checkout.payment.option_gift_card', {
                      amount: giftCardOptions[0].currentValue,
                    })}
                  </Text>
                </Grid>
              </StyledGiftCardContainer>
            ))}
      <Grid
        template="auto 1fr"
        gap="16px"
        m={!noGiftCardOptions ? '15px 0 5px 5px' : '0 0 5px 5px'}
      >
        <SvgIcon name="gift" fontSize="28px" color={isButtonDisabled ? 'GRAY_2' : 'PRIMARY'} />
        <Grid gap="6px">
          <StyledGrid
            gap="12px"
            template={isECardsEnabled ? '3fr 1fr auto' : 'auto 2fr'}
            justify="left"
          >
            <Grid>
              <StyledLabel htmlFor="gift-card-enter-code">
                {t('checkout.payment.add_gift_card')}
                <ToolTip>
                  <SvgIcon name="tooltip" color="PRIMARY" fontSize="16px" m="0 0 0 5px" />
                  <ToolTipContent>{t('checkout.payment.gift_card_tooltip')}</ToolTipContent>
                </ToolTip>
              </StyledLabel>
              <Grid
                className={cx(hasError && css(`input { border-color: ${theme.palette.ERROR}}`))}
              >
                <input
                  data-dd-privacy="mask"
                  id="gift-card-enter-code"
                  placeholder={t('checkout.payment.gift_card_enter_code')}
                  disabled={isInputDisabled}
                  onPaste={(e) => {
                    e.preventDefault()
                    const text = e.clipboardData.getData('text')
                    setCode(
                      isECardsEnabled
                        ? text
                            .replace(/[^0-9A-Za-z]/g, '')
                            .toUpperCase()
                            .slice(0, codeMaxLength)
                        : text.split(/\s+/).join('').slice(0, codeMaxLength)
                    )
                  }}
                  onChange={(e) =>
                    setCode(
                      isECardsEnabled
                        ? e.target.value.replace(/[^0-9A-Za-z]/g, '').toUpperCase()
                        : e.target.value.split(/\s+/).join('')
                    )
                  }
                  value={code}
                  maxLength={codeMaxLength}
                  minLength={codeMinLength}
                  className={cx(formStyles(theme).input)}
                  data-test="gift-card-input"
                  aria-label={t('checkout.payment.gift_card_enter_code')}
                />
              </Grid>
              {hasError && (
                <Text color="ERROR" m="7px 0 0 0">
                  {applyGiftCardError}
                </Text>
              )}
              {isECardsEnabled && (
                <Text color="GRAY_3" m="7px 0 0 0">
                  {t('checkout.payment.gift_card_enter_tooltip')}
                </Text>
              )}
            </Grid>

            {isECardsEnabled && (
              <Grid>
                <StyledLabel htmlFor="gift-card-enter-pin">{t('checkout.payment.PIN')}</StyledLabel>
                <input
                  data-dd-privacy="mask"
                  id="gift-card-enter-pin"
                  placeholder={t('checkout.payment.gift_card_enter_pin')}
                  disabled={isInputDisabled}
                  onPaste={(e) => {
                    e.preventDefault()
                    setPIN(e.clipboardData.getData('text').replace(/\D/g, ''))
                  }}
                  onChange={(e) => setPIN(e.target.value.replace(/\D/g, ''))}
                  value={PIN ?? ''}
                  maxLength={6}
                  className={cx(formStyles(theme).input)}
                  data-test="gift-card-pin-input"
                  aria-label={t('checkout.payment.gift_card_enter_pin')}
                />
              </Grid>
            )}

            <Grid>
              <StyledButton
                data-test="add-gift-card"
                variant="OUTLINE"
                type="button"
                disabled={isButtonDisabled}
                onClick={onSubmit}
              >
                {isCartLoading ? <CircularLoader /> : t('checkout.payment.gift_card_add')}
              </StyledButton>
            </Grid>
          </StyledGrid>
          <StyledReCAPTCHA
            sitekey={process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_KEY ?? ''}
            ref={recaptchaRef}
            size="invisible"
            onChange={() => {
              recaptchaRef.current?.reset()
            }}
          />
        </Grid>
      </Grid>
    </div>
  )
}

export default GiftCardPicker
